import { create } from "zustand";
import { DraftListingData } from "./types";
import { apiFetch } from "@/lib/apiFetch";

interface ListingStore {
  data: DraftListingData | null;
  isLoading: boolean;
  isSaving: boolean;
  lastSaved: number | null;
  error: string | null;
  isUserAgencyOwner: boolean;
  validationErrors: Record<string, string> | null; // 🚀 NEW

  // Actions
  initializeListing: (locale: string) => Promise<void>;
  updateField: <K extends keyof DraftListingData>(
    field: K,
    value: DraftListingData[K],
  ) => void;
  updateMultipleFields: (updates: Partial<DraftListingData>) => void;
  saveDraft: () => Promise<void>;
  clearDraft: () => void;
  setValidationErrors: (errors: Record<string, string> | null) => void; // 🚀 NEW
}

const STORAGE_KEY = "pending_listing_draft";
const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
const AUTOSAVE_DELAY = 5000;

// Keep the timeout ID in memory, entirely outside of React's render cycle
let autosaveTimeout: NodeJS.Timeout | null = null;

export const useListingStore = create<ListingStore>((set, get) => {
  // Helper function to manage the debounced timer
  const triggerAutosave = () => {
    if (autosaveTimeout) {
      clearTimeout(autosaveTimeout);
    }
    autosaveTimeout = setTimeout(() => {
      get().saveDraft();
    }, AUTOSAVE_DELAY);
  };

  return {
    data: null,
    isLoading: true,
    isSaving: false,
    isUserAgencyOwner: false,
    lastSaved: null,
    error: null,
    validationErrors: null,

    initializeListing: async (locale) => {
      set({ isLoading: true, error: null });

      let listingId = "";

      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          const { id, timestamp } = JSON.parse(stored);
          if (Date.now() - timestamp < TWENTY_FOUR_HOURS) {
            listingId = id;
          } else {
            localStorage.removeItem(STORAGE_KEY);
          }
        } catch (e) {
          localStorage.removeItem(STORAGE_KEY);
        }
      }

      try {
        const res = await apiFetch("/api/listings/getPendingListing", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ listing_id: listingId }),
        });

        if (!res.ok) throw new Error("Failed to fetch pending listing");

        const responseData = await res.json();

        set({
          data: { ...responseData.raw_data, user_locale: locale },
          isLoading: false,
          isUserAgencyOwner: responseData.user_is_agency_owner,
        });

        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            id: responseData.listing_id,
            timestamp: Date.now(),
          }),
        );
      } catch (error: any) {
        console.error("Initialization Error:", error);
        set({ isLoading: false, error: error.message });
      }
    },

    setValidationErrors: (errors) => {
      set({ validationErrors: errors });
    },

    updateField: (field, value) => {
      set((state) => {
        if (!state.data) return state;

        // 🚀 Auto-clear the error for this specific field when the user types/clicks
        let newErrors = state.validationErrors;
        if (newErrors && newErrors[field as string]) {
          newErrors = { ...newErrors };
          delete newErrors[field as string];
          if (Object.keys(newErrors).length === 0) newErrors = null;
        }

        return {
          data: { ...state.data, [field]: value },
          validationErrors: newErrors,
        };
      });
      // ONLY trigger autosave when a field is manually updated
      triggerAutosave();
    },

    updateMultipleFields: (updates) => {
      set((state) => {
        if (!state.data) return state;

        // 🚀 Auto-clear errors for all fields being updated
        let newErrors = state.validationErrors;
        if (newErrors) {
          newErrors = { ...newErrors };
          Object.keys(updates).forEach((key) => {
            delete newErrors![key];
          });
          if (Object.keys(newErrors).length === 0) newErrors = null;
        }

        return {
          data: { ...state.data, ...updates },
          validationErrors: newErrors,
        };
      });
      // ONLY trigger autosave when fields are manually updated
      triggerAutosave();
    },

    saveDraft: async () => {
      // Clear the timeout so manual saves don't trigger double-fires
      if (autosaveTimeout) clearTimeout(autosaveTimeout);

      const { data, isSaving } = get();
      if (!data || isSaving) return;

      set({ isSaving: true, error: null });

      const payload = {
        ...data,
        price: Math.round(data.price),
        interior_space: Math.round(data.interior_space),
        lot_size: Math.round(data.lot_size),
        total_structure_area: Math.round(data.total_structure_area),
      };

      try {
        const res = await apiFetch("/api/listings/addPendingListing", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!res.ok) throw new Error("Failed to save draft");

        set({ isSaving: false, lastSaved: Date.now() });

        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ id: data.listing_id, timestamp: Date.now() }),
        );
      } catch (error: any) {
        console.error("Autosave Error:", error);
        set({ isSaving: false, error: error.message });
      }
    },

    clearDraft: () => {
      if (autosaveTimeout) clearTimeout(autosaveTimeout);
      localStorage.removeItem(STORAGE_KEY);
      set({ data: null, lastSaved: null, validationErrors: null });
    },
  };
});
